import get from 'lodash/get';

// QA 4.2 / 3rd path (2026-05-21): the /users page's inline
// Sea Service panel renders rows from `seaService.data` as
// the BE returns them — grouped by verification status, each
// group internally DESC. The user wants strict
// `date_on DESC, date_off DESC` regardless of grouping.
//
// JR 0.10's `find_related_monomorphic_fragments` loses the
// ORDER BY on its second pluck pass; rather than patching JR
// internals, sort on the FE.
//
// Date format coming back is `DD.MM.YYYY`. Pure string compare
// doesn't work (day component sorts before year). Encode as
// integer serial (`YYYYMMDD`) so the comparator can use plain
// subtraction.
const parseDateOnSerial = (s) => {
  if (!s || typeof s !== 'string') return -Infinity;
  const [d, m, y] = s.split('.').map(Number);
  if (!d || !m || !y) return -Infinity;
  return y * 10000 + m * 100 + d;
};

export const sortSeaServiceByDateOnDesc = (rows) => {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const onA = parseDateOnSerial(get(a, 'attributes.date_on'));
    const onB = parseDateOnSerial(get(b, 'attributes.date_on'));
    if (onA !== onB) return onB - onA;
    return (
      parseDateOnSerial(get(b, 'attributes.date_off'))
      - parseDateOnSerial(get(a, 'attributes.date_off'))
    );
  });
};

// CREW-1296 — fields the comment form fills from the profile when it opens
// (`profileValues` holds what it loaded). They are a change only when they
// differ from that. Sending them back as-is made every comment on a seafarer
// with `hot: false` a profile save, and a profile with a namesake refuses any
// save — since CREW-1283 writes the profile first, that refusal took the
// comment with it. `crewell_id` / `maritime_zone_id` were not counted at all,
// so changing only one of them was silently dropped.
const LOADED_FIELDS = ['hot', 'ukr_crewing_id', 'crewell_id', 'maritime_zone_id'];

// `hot` of nil and false is the same "not hot"; an id of nil and '' is the
// same "none".
const comparable = (field, value) => (field === 'hot' ? Boolean(value) : String(value ?? '').trim());

const userChanges = (form = {}) => {
  const loaded = form.profileValues || {};
  const changes = {};

  // Entered, never prefilled: filling one in is the change.
  if (form.readiness_date) changes.readiness_date = form.readiness_date;
  if (form.crewing_status) changes.crewing_status_id = get(form.crewing_status, 'value');
  if (form.wage) changes.wage = form.wage;

  LOADED_FIELDS.forEach((field) => {
    if (comparable(field, form[field]) !== comparable(field, loaded[field])) changes[field] = form[field];
  });

  return changes;
};

export const isUserDataChanged = (form) => Object.keys(userChanges(form)).length > 0;

export const commentData = ({
  userId,
  comment,
  category,
  resource = 'users',
}, author_id) => ({
  body: comment,
  author_id,
  userId,
  resource,
  category,
});

export const commentEventData = ({
  category,
  event_result,
  event_result1,
  event_result2,
  comment_source,
}, comment_id) => ({
  comment_id,
  event_type: category,
  event_result,
  event_result1,
  event_result2,
  comment_source_id: get(comment_source, 'value')
});

// Only what changed (CREW-1296): an unchanged `crewell_id: ''` must not
// overwrite a nil, and an unchanged field must not make the save one the
// backend can refuse for an unrelated reason.
export const userData = (form) => ({
  ...userChanges(form),
  // CREW-1283 — the owner's pool comes off in the same transaction as the
  // status (NFR / Newcomer / Archive / Imported for a seafarer in a pool).
  ...(form.pool_removal ? { pool_removal: form.pool_removal } : {}),
});
